import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { toast } from 'react-toastify';

import { VendorsContext } from '../../context/VendorsContext';
import { DashboardContext, ServiceNowIntegrationContext } from '../../context/DashboardContext';
import SETTINGS from '../../constants/settings';
import BZAPIService from '../../services/BZAPIService';

import { IcoCheck, IcoClock, IcoArrowR, IcoActivity, IcoBolt, IcoFilter } from '../bz/icons';

// ---------- Severity helpers (kept aligned with the legacy chart) ----------
const CRIT = ['blocker', 'severe', 'critical', 'high', 'issue'];
const MED = ['major', 'medium', 'moderate', 'advisory', 'notification', 'warning'];
const LOW = ['minor', 'low', 'informational', 'scheduled change'];

const severityBucket = priority => {
  const k = (priority || '').toLowerCase();
  if (CRIT.some(w => k.includes(w))) return 'crit';
  if (MED.some(w => k.includes(w))) return 'med';
  if (LOW.some(w => k.includes(w))) return 'low';
  return 'other';
};

// ---------- Small primitives ----------

const StatTile = ({ label, value, delta, deltaTone, sub, accent }) => (
  <div className="bz-stat" style={accent ? { borderTop: `3px solid ${accent}` } : undefined}>
    <div className="bz-stat__label">{label}</div>
    <div className="bz-row" style={{ gap: 10, alignItems: 'baseline' }}>
      <div className="bz-stat__value">{value}</div>
      {delta && (
        <span
          style={{
            fontSize: 'var(--fs-xs)',
            fontWeight: 600,
            color:
              deltaTone === 'ok'
                ? 'var(--ok)'
                : deltaTone === 'bad'
                ? 'var(--bad)'
                : deltaTone === 'warn'
                ? 'var(--warn)'
                : 'var(--ink-muted)'
          }}
        >
          {delta}
        </span>
      )}
    </div>
    {sub && <div className="bz-stat__meta">{sub}</div>}
  </div>
);

const SectionCard = ({ title, right, children }) => (
  <div className="bz-card">
    <div
      className="bz-row"
      style={{
        padding: '12px 16px',
        borderBottom: '1px solid var(--line-soft)',
        justifyContent: 'space-between',
        gap: 10
      }}
    >
      <h3 className="bz-h3">{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

// ---------- Dashboard ----------

const Dashboard = () => {
  const navigate = useNavigate();

  const bugOptions = useMemo(
    () => [
      { value: 'newBugsLastWeek', label: 'New bugs · last 7 days' },
      { value: 'updatedBugsLastWeek', label: 'Updated · last 7 days' },
      { value: 'newBugsLastMonth', label: 'New bugs · last 30 days' },
      { value: 'updatedBugsLastMonth', label: 'Updated · last 30 days' },
      { value: 'newBugsLastQuarter', label: 'New bugs · last 90 days' },
      { value: 'updatedBugsLastQuarter', label: 'Updated · last 90 days' }
    ],
    []
  );

  const [vendors] = useContext(VendorsContext);
  const [dashboard, setDashboard] = useContext(DashboardContext);
  const [snIntegration, setSnIntegration] = useContext(ServiceNowIntegrationContext);

  const [loadingServices, setLoadingServices] = useState(false);
  const [selectedBugOption, setSelectedBugOption] = useState(bugOptions[0]);
  const [bzStatus, setBzStatus] = useState({});
  const [showBzStatus, setShowBzStatus] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showAllVendors, setShowAllVendors] = useState(false);
  const [showAllHealth, setShowAllHealth] = useState(false);
  const didInit = useRef(false);

  // One-time SN integration probe + upstream BZ status, mirroring the legacy
  // behavior. The DashboardContext is already populated by BugZeroLayout.
  useEffect(() => {
    if (didInit.current) return;
    if (!vendors?.length || !dashboard?.services?.length) return;
    didInit.current = true;
    setLastUpdated(new Date());
    (async () => {
      if (snIntegration?.isConfigured === null) {
        try {
          const [snCheck, snTest] = await Promise.all([
            BZAPIService.getSetting(SETTINGS.SERVICE_NOW),
            BZAPIService.sNConectionTest()
          ]);
          const isConfigured = !isEmpty(snCheck) && snTest?.valid ? true : false;
          setSnIntegration({ isConfigured, testResult: snTest || {} });
        } catch (e) {
          // Non-fatal — integration tile will just show "not configured".
        }
      }
      try {
        const res = await fetch('https://sls-eus-dev-ec37c2-apim.azure-api.net/getHealth?region=us-east-1');
        const status = await res.json();
        setBzStatus(status);
        if (status?.issuesFound) setShowBzStatus(true);
      } catch (e) {
        // Non-fatal — no upstream banner.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendors, dashboard]);

  const refresh = async () => {
    try {
      setLoadingServices(true);
      const data = await BZAPIService.getDashboard();
      setDashboard(data);
      setLastUpdated(new Date());
    } catch (e) {
      toast.error('Unable to refresh dashboard');
    } finally {
      setLoadingServices(false);
    }
  };

  // ---- Derived data ----
  // Each `dashboard.newBugsLastWeek` etc. is an array of
  // { vendorId, priority, priorityCount, filter }.
  const dataset = useMemo(() => dashboard?.[selectedBugOption.value] || [], [dashboard, selectedBugOption]);
  const services = useMemo(() => dashboard?.services || [], [dashboard]);
  const vendorById = useMemo(() => {
    const m = {};
    (vendors || []).forEach(v => (m[v.id] = v));
    return m;
  }, [vendors]);

  // Aggregate totals across all vendors
  const totals = useMemo(() => {
    let total = 0;
    let crit = 0;
    let med = 0;
    let low = 0;
    dataset.forEach(d => {
      const n = Number(d.priorityCount) || 0;
      total += n;
      const b = severityBucket(d.priority);
      if (b === 'crit') crit += n;
      else if (b === 'med') med += n;
      else if (b === 'low') low += n;
    });
    return { total, crit, med, low };
  }, [dataset]);

  // Per-vendor rows for the findings table
  const vendorRows = useMemo(() => {
    const byVendor = {};
    dataset.forEach(d => {
      const v = vendorById[d.vendorId];
      if (!v || v.type !== 'vendor' || !v.active) return;
      if (!byVendor[d.vendorId]) {
        byVendor[d.vendorId] = { vendor: v, total: 0, crit: 0, med: 0, low: 0 };
      }
      const row = byVendor[d.vendorId];
      const n = Number(d.priorityCount) || 0;
      row.total += n;
      const b = severityBucket(d.priority);
      if (b === 'crit') row.crit += n;
      else if (b === 'med') row.med += n;
      else if (b === 'low') row.low += n;
    });
    return Object.values(byVendor).sort((a, b) => b.total - a.total);
  }, [dataset, vendorById]);

  // Vendor health summary from `services`
  const health = useMemo(() => {
    const byVendor = {};
    services.forEach(s => {
      const v = vendorById[s.vendorId];
      if (!v) return;
      const cur = byVendor[s.vendorId] || { vendor: v, error: 0, ok: 0, total: 0 };
      cur.total += 1;
      if (s.status === 'ERROR') cur.error += 1;
      else cur.ok += 1;
      byVendor[s.vendorId] = cur;
    });
    const rows = Object.values(byVendor).sort(
      (a, b) => b.error - a.error || a.vendor.name.localeCompare(b.vendor.name)
    );
    const degraded = rows.filter(r => r.error > 0).length;
    const totalVendors = rows.length;
    const okCount = totalVendors - degraded;
    const pct = totalVendors ? Math.round((okCount / totalVendors) * 100) : 100;
    return { rows, degraded, totalVendors, okCount, pct };
  }, [services, vendorById]);

  // Status overview = vendors with at least one ERROR-status service
  const errorVendors = useMemo(
    () =>
      services
        .filter(s => s.status === 'ERROR')
        .map(s => ({ service: s, vendor: vendorById[s.vendorId] }))
        .filter(x => x.vendor),
    [services, vendorById]
  );

  const updatedAgo = lastUpdated ? formatAgo(lastUpdated) : 'just now';

  // ---- Loading state ----
  if (!dashboard?.services?.length) {
    return (
      <div style={{ padding: '40px 32px' }}>
        <div className="bz-card" style={{ padding: 48, textAlign: 'center' }}>
          <div className="bz-kicker" style={{ marginBottom: 8 }}>
            Prevent · Loading
          </div>
          <h2 className="bz-h2" style={{ marginBottom: 8 }}>
            Fetching your inventory
          </h2>
          <p className="bz-muted" style={{ margin: 0 }}>
            Pulling vendor health, findings and integration status.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 32px', display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Upstream-status banner (only when BZ services report issues) */}
      {bzStatus?.issuesFound && showBzStatus && bzStatus?.currentIssues?.length > 0 && (
        <div
          className="bz-card"
          style={{
            background: 'var(--bad-soft)',
            borderColor: 'var(--bad-soft)',
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12
          }}
        >
          <IcoBolt size={18} style={{ color: 'var(--bad)', marginTop: 2 }} />
          <div className="bz-grow">
            <div style={{ fontWeight: 600, color: 'var(--bad)', marginBottom: 4 }}>
              BugZero services may be degraded
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--ink)' }}>
              {bzStatus?.currentIssues?.map((i, idx) => (
                <li key={idx + '-' + i.summary}>
                  <strong>{i.summary}</strong>
                </li>
              ))}
            </ul>
          </div>
          <button
            type="button"
            className="bz-btn bz-btn--ghost bz-btn--sm"
            onClick={() => setShowBzStatus(false)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Page head */}
      <div className="bz-section-head" style={{ marginBottom: 0 }}>
        <div>
          <div className="bz-kicker" style={{ color: 'var(--prevent)' }}>
            Prevent · Operations
          </div>
          <h1 className="bz-h1" style={{ marginTop: 4 }}>
            Dashboard
          </h1>
          <div className="bz-muted" style={{ fontSize: 'var(--fs-md)', marginTop: 4 }}>
            Findings, vendor health, and remediation activity across your inventory.
          </div>
        </div>
        <div className="bz-row" style={{ gap: 8 }}>
          <span className="bz-pill">
            <IcoClock size={11} /> Updated {updatedAgo}
          </span>
          <button type="button" className="bz-btn bz-btn--sm" onClick={refresh} disabled={loadingServices}>
            {loadingServices ? 'Refreshing…' : 'Refresh'}
          </button>
          <button
            type="button"
            className="bz-btn bz-btn--sm bz-btn--primary"
            onClick={() => navigate('/vendor-health')}
          >
            Vendor health →
          </button>
        </div>
      </div>

      {/* Stat strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
        <StatTile
          label={selectedBugOption.label}
          value={totals.total.toLocaleString()}
          sub={`across ${vendorRows.length} ${vendorRows.length === 1 ? 'vendor' : 'vendors'}`}
          accent="var(--prevent)"
        />
        <StatTile
          label="Critical / High"
          value={totals.crit.toLocaleString()}
          delta={totals.crit > 0 ? 'needs triage' : 'none open'}
          deltaTone={totals.crit > 0 ? 'bad' : 'ok'}
          sub={`${totals.med.toLocaleString()} moderate · ${totals.low.toLocaleString()} low`}
          accent="var(--bad)"
        />
        <StatTile
          label="Vendor health"
          value={`${health.pct}%`}
          delta={health.degraded > 0 ? `${health.degraded} degraded` : 'all nominal'}
          deltaTone={health.degraded > 0 ? 'warn' : 'ok'}
          sub={`${health.okCount} of ${health.totalVendors} vendors nominal`}
          accent={health.degraded > 0 ? 'var(--warn)' : 'var(--ok)'}
        />
        <StatTile
          label="ServiceNow"
          value={
            snIntegration?.isConfigured ? 'Connected' : snIntegration?.isConfigured === false ? 'Not configured' : '—'
          }
          sub={snIntegration?.isConfigured ? 'Routing findings to incidents' : 'Configure to route findings'}
          accent={snIntegration?.isConfigured ? 'var(--ok)' : 'var(--ink-faint)'}
        />
      </div>

      {/* Two-column body */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 16, alignItems: 'start' }}>
        {/* Findings by vendor */}
        <SectionCard
          title="Findings by vendor"
          right={
            <div className="bz-row" style={{ gap: 8 }}>
              <select
                className="bz-input"
                value={selectedBugOption.value}
                onChange={e => setSelectedBugOption(bugOptions.find(o => o.value === e.target.value) || bugOptions[0])}
                style={{ minHeight: 30, padding: '4px 10px' }}
              >
                {bugOptions.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <button type="button" className="bz-btn bz-btn--sm bz-btn--ghost" disabled>
                <IcoFilter size={13} /> Filter
              </button>
            </div>
          }
        >
          {vendorRows.length === 0 ? (
            <div style={{ padding: 24, color: 'var(--ink-muted)', textAlign: 'center' }}>
              No findings for the selected window.
            </div>
          ) : (
            <table className="bz-table">
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th>Total</th>
                  <th>Critical / High</th>
                  <th>Moderate</th>
                  <th>Low</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(showAllVendors ? vendorRows : vendorRows.slice(0, 10)).map(row => {
                  const max = Math.max(totals.total, 1);
                  const ratio = row.total / max;
                  return (
                    <tr key={row.vendor.id}>
                      <td>
                        <div className="bz-row" style={{ gap: 10 }}>
                          <div
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: 6,
                              background: 'var(--bg-tan)',
                              color: 'var(--bz-blue-2)',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 600,
                              fontSize: 11
                            }}
                          >
                            {row.vendor.name?.[0]?.toUpperCase() || 'V'}
                          </div>
                          <div>
                            <div style={{ fontWeight: 500 }}>{row.vendor.name}</div>
                            <div className="bz-bar" style={{ width: 120, marginTop: 4 }}>
                              <span
                                style={{
                                  width: `${ratio * 100}%`,
                                  background: row.crit > 0 ? 'var(--bad)' : row.med > 0 ? 'var(--warn)' : 'var(--ok)'
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="bz-mono" style={{ fontWeight: 600 }}>
                        {row.total.toLocaleString()}
                      </td>
                      <td>
                        {row.crit > 0 ? (
                          <span className="bz-pill bz-pill--bad bz-mono">{row.crit.toLocaleString()}</span>
                        ) : (
                          <span className="bz-muted bz-mono">0</span>
                        )}
                      </td>
                      <td>
                        {row.med > 0 ? (
                          <span className="bz-pill bz-pill--warn bz-mono">{row.med.toLocaleString()}</span>
                        ) : (
                          <span className="bz-muted bz-mono">0</span>
                        )}
                      </td>
                      <td>
                        {row.low > 0 ? (
                          <span className="bz-pill bz-pill--ok bz-mono">{row.low.toLocaleString()}</span>
                        ) : (
                          <span className="bz-muted bz-mono">0</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <a
                          onClick={() => navigate(`/vendors/${row.vendor.id}/configuration`)}
                          style={{ fontSize: 'var(--fs-sm)', cursor: 'pointer' }}
                        >
                          Configure →
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {vendorRows.length > 10 && (
            <div
              className="bz-row"
              style={{
                padding: '10px 16px',
                borderTop: '1px solid var(--line-soft)',
                justifyContent: 'space-between'
              }}
            >
              <span className="bz-muted" style={{ fontSize: 'var(--fs-sm)' }}>
                {showAllVendors
                  ? `Showing all ${vendorRows.length} vendors`
                  : `Showing 10 of ${vendorRows.length} vendors`}
              </span>
              <a
                onClick={() => setShowAllVendors(s => !s)}
                style={{
                  fontSize: 'var(--fs-sm)',
                  color: 'var(--info)',
                  cursor: 'pointer',
                  fontWeight: 500
                }}
              >
                {showAllVendors ? 'Show top 10 ←' : 'Show all vendors →'}
              </a>
            </div>
          )}
        </SectionCard>

        {/* Right rail */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Vendor health */}
          <SectionCard
            title="Vendor health"
            right={
              health.degraded > 0 ? (
                <span className="bz-pill bz-pill--warn">
                  <span className="bz-dot" /> {health.degraded} degraded
                </span>
              ) : (
                <span className="bz-pill bz-pill--ok">
                  <IcoCheck size={11} /> All nominal
                </span>
              )
            }
          >
            <div style={{ padding: '8px 0' }}>
              {health.rows.length === 0 ? (
                <div style={{ padding: '12px 16px', color: 'var(--ink-muted)', fontSize: 'var(--fs-sm)' }}>
                  No vendor services configured.
                </div>
              ) : (
                (showAllHealth ? health.rows : health.rows.slice(0, 8)).map(r => (
                  <div
                    key={r.vendor.id}
                    className="bz-row"
                    style={{
                      padding: '8px 16px',
                      justifyContent: 'space-between',
                      cursor: 'pointer'
                    }}
                    onClick={() => navigate(`/vendors/${r.vendor.id}/configuration`)}
                  >
                    <div className="bz-row" style={{ gap: 8, minWidth: 0 }}>
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 999,
                          background: r.error > 0 ? 'var(--warn)' : 'var(--ok)',
                          flexShrink: 0
                        }}
                      />
                      <span style={{ fontWeight: 500 }} className="bz-truncate">
                        {r.vendor.name}
                      </span>
                    </div>
                    <span className="bz-muted bz-mono" style={{ fontSize: 'var(--fs-sm)' }}>
                      {r.error > 0 ? `${r.error} err` : `${r.ok}/${r.total}`}
                    </span>
                  </div>
                ))
              )}
            </div>
            {health.rows.length > 8 && (
              <div
                className="bz-row"
                style={{ padding: '10px 16px', borderTop: '1px solid var(--line-soft)', justifyContent: 'flex-end' }}
              >
                <a
                  onClick={() => setShowAllHealth(s => !s)}
                  style={{ fontSize: 'var(--fs-sm)', color: 'var(--info)', cursor: 'pointer', fontWeight: 500 }}
                >
                  {showAllHealth ? 'Show fewer ←' : `Show all ${health.rows.length} →`}
                </a>
              </div>
            )}
          </SectionCard>

          {/* Status overview — vendors currently in ERROR */}
          <SectionCard
            title="Status overview"
            right={
              errorVendors.length > 0 ? (
                <span className="bz-pill bz-pill--bad">
                  <span className="bz-dot" /> {errorVendors.length} issue
                  {errorVendors.length === 1 ? '' : 's'}
                </span>
              ) : (
                <span className="bz-pill bz-pill--ok">
                  <IcoCheck size={11} /> All systems go
                </span>
              )
            }
          >
            {errorVendors.length === 0 ? (
              <div
                style={{
                  padding: '14px 16px',
                  color: 'var(--ink-muted)',
                  fontSize: 'var(--fs-sm)'
                }}
              >
                No upstream disruptions detected across external services in the last 24h.
              </div>
            ) : (
              <div style={{ padding: '8px 0' }}>
                {errorVendors.slice(0, 5).map(({ vendor, service }) => (
                  <div
                    key={`${vendor.id}-${service.bugServiceName || service.name || ''}`}
                    className="bz-row"
                    style={{
                      padding: '10px 16px',
                      borderBottom: '1px solid var(--line-soft)',
                      gap: 10,
                      alignItems: 'flex-start',
                      cursor: 'pointer'
                    }}
                    onClick={() => navigate(`/vendors/${vendor.id}/configuration`)}
                  >
                    <IcoActivity size={14} style={{ color: 'var(--bad)', marginTop: 2 }} />
                    <div className="bz-grow" style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 500 }}>{vendor.name}</div>
                      <div className="bz-muted" style={{ fontSize: 'var(--fs-xs)' }}>
                        {service?.vendorData?.bugCount
                          ? `Error — ${service.vendorData.bugCount} bug updates published`
                          : 'Integration error'}
                      </div>
                    </div>
                    <IcoArrowR size={13} style={{ color: 'var(--ink-faint)', marginTop: 4 }} />
                  </div>
                ))}
                {errorVendors.length > 5 && (
                  <div className="bz-row" style={{ padding: '10px 16px', justifyContent: 'flex-end' }}>
                    <a
                      onClick={() => navigate('/vendor-health')}
                      style={{ fontSize: 'var(--fs-sm)', color: 'var(--info)', cursor: 'pointer' }}
                    >
                      View all errors →
                    </a>
                  </div>
                )}
              </div>
            )}
          </SectionCard>
        </div>
      </div>
    </div>
  );
};

function formatAgo(date) {
  const ms = Date.now() - date.getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

StatTile.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  delta: PropTypes.string,
  deltaTone: PropTypes.oneOf(['ok', 'bad', 'warn', 'muted']),
  sub: PropTypes.string,
  accent: PropTypes.string
};

SectionCard.propTypes = {
  title: PropTypes.string,
  right: PropTypes.node,
  children: PropTypes.node
};

export default Dashboard;
